import React, { useEffect, useCallback } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import queryString from 'query-string';

// Stores
import useSearchStore from '../../store/useSearchStore';
import useCartStore from '../../store/useCartStore';
import useComparisonStore from '../../store/useComparisonStore';

// Components
import InfiniteList from '../../layout/components/InfiniteList/InfiniteList';
import SearchLayout from '../../components/SearchLayout/SearchLayout';
import SearchTopBar from '../../components/SearchTopBar/SearchTopBar';
import SearchSidebar from '../../components/SearchSidebar/SearchSidebar';
import SearchResults from '../../components/SearchResults/SearchResults';
import RecentSearchesSection from '../../components/RecentSearchesSection/RecentSearchesSection';
import ComparePanel from '../../components/ComparePanel/ComparePanel';
import CompareButton from '../../components/CompareButton/CompareButton';

// Alerts
import * as alertify from '../../components/Alerts/lib/alertify';

// Routes
import routeCartPage from '../../pages/CartPage/route';

const SearchContainer = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const params = queryString.parse(location.search);

  // Search store
  const {
    tyres,
    hasMore,
    recentSearches,
    recentSearchesEnabled,
    setSearchValue,
    setVehicleSearch,
    addRecentSearch,
    removeRecentSearch,
    clearRecentSearches,
    initialize,
    fetchTyres,
    loadMore,
    createInquiry,
    addToCartLoadingId,
    setAddToCartLoadingId,
    getQuantity,
  } = useSearchStore();

  // Cart store
  const { addItem: addToCart, items: cartItems } = useCartStore();

  // Comparison store
  const {
    tyres: comparisonTyres,
    isOpen: isComparePanelOpen,
    openPanel: handleOpenComparePanel,
    closePanel: handleCloseComparePanel,
    addTyre,
    removeTyre: handleRemoveFromCompare,
    clear: handleClearComparison,
    isFull,
  } = useComparisonStore();

  // Initialize on mount
  useEffect(() => {
    initialize(params);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle add to cart
  const handleAddToCart = useCallback(
    async (id) => {
      const tyre = tyres.find((t) => t._id === id);
      if (!tyre || addToCartLoadingId) return;

      setAddToCartLoadingId(id);

      try {
        const quantity = getQuantity(id);
        
        // Check if item already exists in cart to get combined quantity
        const existingCartItem = cartItems.find((c) => c._id === id);
        const existingQuantity = existingCartItem?.cartQuantity || 0;
        const totalQuantity = quantity + existingQuantity;
        
        const tyreWithQuantity = {
          ...tyre,
          cartQuantity: totalQuantity, // Use total for inquiry
        };

        const result = await createInquiry(tyreWithQuantity);
        const { deliveryDate, scheduleDetails, error, isNotInStock, notInStockQuantity } = result;

        if (error) {
          alertify.error(error);
          setAddToCartLoadingId('');
          return;
        }

        const item = {
          ...tyre,
          cartQuantity: quantity, // Add only the new quantity to cart
          confirmedDeliveryDate: tyre.selectedDeliveryDate || deliveryDate,
          selectedDeliveryDate: tyre.selectedDeliveryDate || null, // Preserve user's selection
          scheduleDetails: scheduleDetails || [], // But use scheduleDetails for total
          isNotInStock,
          notInStockQuantity,
        };
        addToCart(item);

        setAddToCartLoadingId('');
        alertify.success(t('productAddedToCart'));
        history.push(routeCartPage());
      } catch (error) {
        setAddToCartLoadingId('');
        alertify.error(t('errorHappened'));
      }
    },
    [
      tyres,
      cartItems,
      addToCartLoadingId,
      setAddToCartLoadingId,
      getQuantity,
      createInquiry,
      addToCart,
      history,
      t,
    ]
  );

  // Handle toggle compare
  const handleToggleCompare = useCallback(
    (tyre) => {
      const isAlreadyInComparison = comparisonTyres.some(
        (t) => t._id === tyre._id
      );

      if (isAlreadyInComparison) {
        handleRemoveFromCompare(tyre._id);
      } else {
        if (isFull()) {
          alertify.warning(t('maxComparisonReached'));
          return;
        }
        addTyre(tyre);
      }
    },
    [comparisonTyres, handleRemoveFromCompare, isFull, addTyre, t]
  );

  // Handle recent search selection
  const handleSelectRecentSearch = useCallback(
    (search) => {
      // Handle vehicle search object
      if (typeof search === 'object' && search.type === 'vehicle') {
        setSearchValue(search.eans);
        setVehicleSearch(search.vehicleInfo);
        fetchTyres({ refresh: true });
        return;
      }
      
      // Handle plain text search
      setSearchValue(search);
      addRecentSearch(search);
      fetchTyres({ refresh: true });
    },
    [setSearchValue, setVehicleSearch, addRecentSearch, fetchTyres]
  );

  return (
    <>
      <SearchLayout
        topBar={<SearchTopBar />}
        sidebar={<SearchSidebar />}
      >
        {recentSearchesEnabled && recentSearches.length > 0 && (
          <RecentSearchesSection
            searches={recentSearches}
            onSelectSearch={handleSelectRecentSearch}
            onRemoveSearch={removeRecentSearch}
            onClearAll={clearRecentSearches}
          />
        )}
        <InfiniteList 
          onLoad={loadMore} 
          hasMore={hasMore} 
          initialLoad={false}
        >
          <SearchResults
            onAddToCart={handleAddToCart}
            onToggleCompare={handleToggleCompare}
          />
        </InfiniteList>
      </SearchLayout>

      <CompareButton
        count={comparisonTyres.length}
        onClick={handleOpenComparePanel}
        pulse={comparisonTyres.length > 0 && !isComparePanelOpen}
      />

      <ComparePanel
        isOpen={isComparePanelOpen}
        onClose={handleCloseComparePanel}
        tyres={comparisonTyres}
        onRemoveTyre={handleRemoveFromCompare}
        onClearAll={() => {
          handleClearComparison();
          alertify.success(t('comparisonCleared'));
        }}
      />
    </>
  );
};

export default SearchContainer;
